import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiFetch } from "./api/client";

const number = (value) =>
  Number(value || 0).toLocaleString();

const dateTime = (value) => {
  if (!value) return "Not reported";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
};

const capabilityRows = [
  ["Connection metadata", true],
  ["Flow / service analysis", true],
  ["Process correlation", true],
  ["DNS-associated traffic", true],
  ["DNS query inspection", false],
  ["Packet inspection", false],
  ["Deep packet inspection", false],
  ["PCAP capture", false],
];

function scopedUrl(
  path,
  companyId,
  deviceUuid,
  extra = {}
) {
  const params = new URLSearchParams();

  if (companyId) {
    params.set(
      "company_id",
      String(companyId)
    );
  }

  if (deviceUuid) {
    params.set(
      "device_uuid",
      String(deviceUuid)
    );
  }

  Object.entries(extra).forEach(
    ([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        params.set(
          key,
          String(value)
        );
      }
    }
  );

  const query = params.toString();

  return query
    ? `${path}?${query}`
    : path;
}

async function readJson(response) {
  let body = null;

  try {
    body = await response.json();
  } catch (_) {
    body = null;
  }

  if (!response.ok) {
    const detail =
      body?.detail ||
      body?.message ||
      `HTTP ${response.status}`;

    throw new Error(detail);
  }

  return body;
}

export default function NetworkIntelligencePanel({
  companyId,
  deviceUuid,
}) {
  const [summary, setSummary] = useState(null);
  const [protocols, setProtocols] = useState([]);
  const [services, setServices] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [signalSummary, setSignalSummary] = useState(null);
  const [signals, setSignals] = useState([]);
  const [correlationSummary, setCorrelationSummary] = useState(null);
  const [correlationLoading, setCorrelationLoading] = useState(false);
  const [correlationError, setCorrelationError] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async () => {
    if (!companyId || !deviceUuid) {
      setSummary(null);
      setProtocols([]);
      setServices([]);
      setDestinations([]);
      setProcesses([]);
      setTimeline([]);
      setSignalSummary(null);
      setSignals([]);
      setError("");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const urls = [
        scopedUrl(
          "/network-protection/intelligence/summary",
          companyId,
          deviceUuid
        ),
        scopedUrl(
          "/network-protection/intelligence/protocols",
          companyId,
          deviceUuid,
          { limit: 50 }
        ),
        scopedUrl(
          "/network-protection/intelligence/services",
          companyId,
          deviceUuid,
          { limit: 100 }
        ),
        scopedUrl(
          "/network-protection/intelligence/destinations",
          companyId,
          deviceUuid,
          { limit: 50 }
        ),
        scopedUrl(
          "/network-protection/intelligence/processes",
          companyId,
          deviceUuid,
          { limit: 50 }
        ),
        scopedUrl(
          "/network-protection/intelligence/timeline",
          companyId,
          deviceUuid,
          {
            bucket: "day",
            limit: 30,
          }
        ),
        scopedUrl(
          "/network-protection/intelligence/signals/summary",
          companyId,
          deviceUuid
        ),
        scopedUrl(
          "/network-protection/intelligence/signals",
          companyId,
          deviceUuid,
          { limit: 100 }
        ),
      ];

      const responses = await Promise.all(
        urls.map((url) => apiFetch(url))
      );

      const [
        summaryBody,
        protocolBody,
        serviceBody,
        destinationBody,
        processBody,
        timelineBody,
        signalSummaryBody,
        signalBody,
      ] = await Promise.all(
        responses.map(readJson)
      );

      setSummary(summaryBody || null);
      setProtocols(
        Array.isArray(protocolBody?.items)
          ? protocolBody.items
          : []
      );
      setServices(
        Array.isArray(serviceBody?.items)
          ? serviceBody.items
          : []
      );
      setDestinations(
        Array.isArray(destinationBody?.items)
          ? destinationBody.items
          : []
      );
      setProcesses(
        Array.isArray(processBody?.items)
          ? processBody.items
          : []
      );
      setTimeline(
        Array.isArray(timelineBody?.items)
          ? timelineBody.items
          : []
      );

      setSignalSummary(
        signalSummaryBody || null
      );

      setSignals(
        Array.isArray(signalBody?.items)
          ? signalBody.items
          : []
      );

      setLoadedAt(new Date());
    } catch (loadError) {
      console.error(
        "Network intelligence load error:",
        loadError
      );

      setError(
        loadError?.message ||
          "Unable to load Network Protection intelligence."
      );
    } finally {
      setLoading(false);
    }
  }, [companyId, deviceUuid]);

  // R18-P3 correlation loads independently from the existing
  // P1/P2 refresh path so slow correlation analysis cannot hold
  // the entire Network Intelligence panel in "Refreshing...".
  useEffect(() => {
    let cancelled = false;

    if (!companyId || !deviceUuid) {
      setCorrelationSummary(null);
      setCorrelationLoading(false);
      setCorrelationError("");

      return () => {
        cancelled = true;
      };
    }

    const loadCorrelationSummary = async () => {
      setCorrelationLoading(true);
      setCorrelationError("");

      try {
        const url = scopedUrl(
          "/network-protection/intelligence/correlation/summary",
          companyId,
          deviceUuid
        );

        const response = await apiFetch(url);

        if (!response.ok) {
          throw new Error(
            `Correlation summary request failed (${response.status})`
          );
        }

        const body = await response.json();

        if (!cancelled) {
          setCorrelationSummary(
            body || null
          );
        }
      } catch (error) {
        if (!cancelled) {
          setCorrelationSummary(null);
          setCorrelationError(
            error?.message ||
              "Unable to load correlation summary."
          );
        }
      } finally {
        if (!cancelled) {
          setCorrelationLoading(false);
        }
      }
    };

    loadCorrelationSummary();

    return () => {
      cancelled = true;
    };
  }, [companyId, deviceUuid]);


  useEffect(() => {
    load();
  }, [load]);

  const serviceTotal = useMemo(
    () =>
      services.reduce(
        (sum, row) =>
          sum + Number(row?.event_count || 0),
        0
      ),
    [services]
  );

  if (!companyId || !deviceUuid) {
    return (
      <section className="r18-intelligence-panel">
        <div className="r18-intelligence-header">
          <div>
            <span className="r18-kicker">
              R18 NETWORK INTELLIGENCE
            </span>

            <h2>Traffic Intelligence</h2>

            <p>
              Select a managed endpoint to load
              tenant-scoped network intelligence.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="r18-intelligence-panel">
      <div className="r18-intelligence-header">
        <div>
          <span className="r18-kicker">
            R18 NETWORK INTELLIGENCE
          </span>

          <h2>
            Flow, Protocol &amp; Service Intelligence
          </h2>

          <p>
            Read-only analytics generated from
            connection metadata already collected
            from this endpoint.
          </p>
        </div>

        <div className="r18-header-actions">
          <span
            className={`r18-freshness ${
              summary?.data_freshness || "unknown"
            }`}
          >
            Data:{" "}
            {summary?.data_freshness || "unknown"}
          </span>

          <button
            type="button"
            onClick={load}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {error ? (
        <div className="r18-error">
          {error}
        </div>
      ) : null}

      <div className="r18-metrics">
        <R18Metric
          title="Network Events"
          value={number(summary?.total_events)}
          detail="Tenant + endpoint scoped"
        />

        <R18Metric
          title="Remote IPs"
          value={number(
            summary?.distinct_remote_ips
          )}
          detail="Distinct observed destinations"
        />

        <R18Metric
          title="Processes"
          value={number(
            summary?.distinct_processes
          )}
          detail="Process ↔ network correlation"
        />

        <R18Metric
          title="High Events"
          value={number(summary?.high_events)}
          detail="Historical severity observations"
        />

        <R18Metric
          title="Firewall Observations"
          value={number(
            summary?.firewall_observations
          )}
          detail="Kept separate from runtime data"
        />

        <R18Metric
          title="Other Observations"
          value={number(
            summary?.non_firewall_observations
          )}
          detail="Runtime / service observations"
        />
      </div>

      <div className="r18-freshness-banner">
        <strong>Telemetry freshness</strong>

        <span>
          Latest observation:{" "}
          {dateTime(
            summary?.latest_observed_at
          )}
        </span>

        <span>
          Inspection level:{" "}
          {summary?.inspection_level ||
            "connection_metadata"}
        </span>

        {loadedAt ? (
          <span>
            Dashboard refreshed:{" "}
            {loadedAt.toLocaleTimeString()}
          </span>
        ) : null}
      </div>

      <div className="r18-grid-two">
        <R18Table
          title="Protocols"
          subtitle="Observed transport classifications"
          columns={[
            ["Protocol", "protocol"],
            ["Events", "event_count", number],
            [
              "Remote IPs",
              "remote_ip_count",
              number,
            ],
          ]}
          rows={protocols}
        />

        <R18Table
          title="Port-associated Services"
          subtitle={`${number(
            serviceTotal
          )} classified observations`}
          columns={[
            ["Service", "service_name"],
            ["Port", "service_port"],
            ["Type", "observation_type"],
            ["Protocol", "protocol"],
            ["Events", "event_count", number],
          ]}
          rows={services}
        />
      </div>

      <div className="r18-grid-two">
        <R18Table
          title="Top Destinations"
          subtitle="Remote endpoints observed in telemetry"
          columns={[
            ["Remote IP", "remote_ip"],
            ["Port", "remote_port"],
            ["Service", "service_name"],
            ["Process", "process_name"],
            ["Events", "event_count", number],
          ]}
          rows={destinations}
        />

        <R18Table
          title="Process ↔ Network Correlation"
          subtitle="Processes associated with network observations"
          columns={[
            ["Process", "process_name"],
            ["Events", "event_count", number],
            [
              "Remote IPs",
              "remote_ip_count",
              number,
            ],
            [
              "Firewall",
              "firewall_observations",
              number,
            ],
            [
              "Other",
              "runtime_or_other_observations",
              number,
            ],
          ]}
          rows={processes}
        />
      </div>

      <BehavioralSignalsPanel
        summary={signalSummary}
        signals={signals}
        loading={loading}
      />

      <CorrelationPanel
        summary={correlationSummary}
        loading={correlationLoading}
        error={correlationError}
        companyId={companyId}
        deviceUuid={deviceUuid}
      />

      <div className="r18-grid-two">
        <R18Timeline rows={timeline} />

        <InspectionCapabilities />
      </div>

      <style>{`
        .r18-intelligence-panel {
          margin: 22px 0;
          padding: 22px;
          border: 1px solid #dbe7f5;
          border-radius: 22px;
          background:
            linear-gradient(
              180deg,
              #f8fbff 0%,
              #ffffff 100%
            );
          box-shadow:
            0 14px 35px rgba(15, 23, 42, 0.06);
        }

        .r18-intelligence-header {
          display: flex;
          justify-content: space-between;
          gap: 20px;
          align-items: flex-start;
          margin-bottom: 18px;
        }

        .r18-intelligence-header h2 {
          margin: 5px 0 7px;
          font-size: 22px;
          color: #0f172a;
        }

        .r18-intelligence-header p {
          margin: 0;
          max-width: 760px;
          color: #64748b;
          line-height: 1.55;
        }

        .r18-kicker {
          color: #2563eb;
          font-size: 11px;
          font-weight: 900;
          letter-spacing: .12em;
        }

        .r18-header-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .r18-header-actions button {
          border: 0;
          padding: 9px 13px;
          border-radius: 10px;
          background: #0f172a;
          color: #fff;
          font-weight: 800;
          cursor: pointer;
        }

        .r18-header-actions button:disabled {
          opacity: .55;
          cursor: wait;
        }

        .r18-freshness {
          display: inline-flex;
          padding: 7px 11px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 900;
          background: #e2e8f0;
          color: #334155;
          text-transform: uppercase;
        }

        .r18-freshness.current {
          background: #dcfce7;
          color: #166534;
        }

        .r18-freshness.delayed {
          background: #fef3c7;
          color: #92400e;
        }

        .r18-freshness.stale {
          background: #fee2e2;
          color: #991b1b;
        }

        .r18-error {
          margin-bottom: 16px;
          padding: 12px 14px;
          border: 1px solid #fecaca;
          border-radius: 12px;
          background: #fef2f2;
          color: #991b1b;
          font-weight: 700;
        }

        .r18-metrics {
          display: grid;
          grid-template-columns:
            repeat(
              auto-fit,
              minmax(160px, 1fr)
            );
          gap: 12px;
          margin-bottom: 16px;
        }

        .r18-metric {
          padding: 16px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #fff;
        }

        .r18-metric span {
          display: block;
          color: #64748b;
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 6px;
        }

        .r18-metric strong {
          display: block;
          color: #0f172a;
          font-size: 22px;
          margin-bottom: 4px;
        }

        .r18-metric small {
          color: #94a3b8;
          line-height: 1.35;
        }

        .r18-freshness-banner {
          display: flex;
          flex-wrap: wrap;
          gap: 10px 18px;
          padding: 12px 14px;
          margin-bottom: 16px;
          border-radius: 14px;
          background: #0f172a;
          color: #cbd5e1;
          font-size: 12px;
        }

        .r18-freshness-banner strong {
          color: #fff;
        }

        .r18-grid-two {
          display: grid;
          grid-template-columns:
            repeat(
              2,
              minmax(0, 1fr)
            );
          gap: 16px;
          margin-top: 16px;
        }

        .r18-card {
          min-width: 0;
          border: 1px solid #e2e8f0;
          border-radius: 18px;
          background: #fff;
          overflow: hidden;
        }

        .r18-card-head {
          padding: 15px 16px;
          border-bottom: 1px solid #e2e8f0;
        }

        .r18-card-head h3 {
          margin: 0 0 3px;
          color: #0f172a;
          font-size: 16px;
        }

        .r18-card-head p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
        }

        .r18-table-wrap {
          max-height: 360px;
          overflow: auto;
        }

        .r18-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 520px;
        }

        .r18-table th,
        .r18-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #f1f5f9;
          text-align: left;
          font-size: 12px;
          vertical-align: top;
        }

        .r18-table th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f8fafc;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: .04em;
        }

        .r18-table td {
          color: #334155;
        }

        .r18-empty {
          padding: 22px;
          color: #64748b;
          text-align: center;
        }

        .r18-capabilities {
          display: grid;
          gap: 8px;
          padding: 14px;
        }

        .r18-capability {
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
          padding: 10px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
        }

        .r18-capability strong {
          font-size: 13px;
          color: #334155;
        }

        .r18-capability span {
          padding: 5px 8px;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 900;
        }

        .r18-capability .yes {
          background: #dcfce7;
          color: #166534;
        }

        .r18-capability .no {
          background: #f1f5f9;
          color: #64748b;
        }

        .r18-timeline {
          padding: 14px;
          display: grid;
          gap: 9px;
          max-height: 380px;
          overflow: auto;
        }

        .r18-timeline-row {
          padding: 11px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #f8fafc;
        }

        .r18-timeline-top {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          color: #334155;
          font-size: 12px;
          font-weight: 800;
        }

        .r18-timeline-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 7px 12px;
          margin-top: 7px;
          color: #64748b;
          font-size: 11px;
        }


        .r18-behavioral-section {
          margin-top: 18px;
          padding: 18px;
          border: 1px solid #dbe7f5;
          border-radius: 18px;
          background: #fff;
        }

        .r18-behavioral-head {
          display: flex;
          justify-content: space-between;
          gap: 18px;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .r18-behavioral-head h3 {
          margin: 5px 0 6px;
          color: #0f172a;
          font-size: 18px;
        }

        .r18-behavioral-head p {
          margin: 0;
          color: #64748b;
          max-width: 760px;
          line-height: 1.5;
          font-size: 13px;
        }

        .r18-signal-state {
          display: inline-flex;
          padding: 7px 10px;
          border-radius: 999px;
          background: #e2e8f0;
          color: #334155;
          font-size: 10px;
          font-weight: 900;
          white-space: nowrap;
        }

        .r18-signal-state.insufficient_current_evidence {
          background: #fef3c7;
          color: #92400e;
        }

        .r18-signal-state.no_candidates_observed {
          background: #dcfce7;
          color: #166534;
        }

        .r18-signal-state.historical_candidates {
          background: #ffedd5;
          color: #9a3412;
        }

        .r18-signal-state.candidates_observed {
          background: #fee2e2;
          color: #991b1b;
        }

        .r18-signal-metrics {
          display: grid;
          grid-template-columns:
            repeat(
              auto-fit,
              minmax(160px, 1fr)
            );
          gap: 12px;
          margin-bottom: 14px;
        }

        .r18-signal-grid {
          display: grid;
          grid-template-columns:
            repeat(
              auto-fit,
              minmax(145px, 1fr)
            );
          gap: 10px;
          margin-bottom: 14px;
        }

        .r18-signal-card {
          padding: 13px;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          background: #f8fafc;
        }

        .r18-signal-card span {
          display: block;
          color: #64748b;
          font-size: 11px;
          font-weight: 800;
          margin-bottom: 6px;
        }

        .r18-signal-card strong {
          display: block;
          color: #0f172a;
          font-size: 20px;
        }

        .r18-signal-card small {
          color: #94a3b8;
          font-size: 10px;
        }

        .r18-evidence-notice {
          margin-bottom: 14px;
          padding: 14px;
          border: 1px solid #fde68a;
          border-radius: 14px;
          background: #fffbeb;
        }

        .r18-evidence-notice strong {
          color: #92400e;
        }

        .r18-evidence-notice p {
          margin: 6px 0 0;
          color: #78350f;
          font-size: 12px;
          line-height: 1.5;
        }

        .r18-no-signals {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 15px;
          border: 1px dashed #cbd5e1;
          border-radius: 14px;
          background: #f8fafc;
          color: #475569;
        }

        .r18-no-signals strong {
          color: #0f172a;
        }

        .r18-no-signals span {
          font-size: 12px;
        }

        .r18-behavioral-truth {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 14px;
        }

        .r18-behavioral-truth span {
          padding: 6px 9px;
          border-radius: 999px;
          background: #f1f5f9;
          color: #475569;
          font-size: 10px;
          font-weight: 800;
        }

        @media (max-width: 980px) {
          .r18-grid-two {
            grid-template-columns: 1fr;
          }

          .r18-intelligence-header {
            flex-direction: column;
          }

          .r18-header-actions {
            justify-content: flex-start;
          }
        }
      `}</style>
    </section>
  );
}

function R18Metric({
  title,
  value,
  detail,
}) {
  return (
    <div className="r18-metric">
      <span>{title}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function R18Table({
  title,
  subtitle,
  columns,
  rows,
}) {
  return (
    <div className="r18-card">
      <div className="r18-card-head">
        <h3>{title}</h3>
        <p>{subtitle}</p>
      </div>

      {rows.length === 0 ? (
        <div className="r18-empty">
          No observations available.
        </div>
      ) : (
        <div className="r18-table-wrap">
          <table className="r18-table">
            <thead>
              <tr>
                {columns.map(([label]) => (
                  <th key={label}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  {columns.map(
                    ([
                      label,
                      key,
                      formatter,
                    ]) => (
                      <td key={label}>
                        {formatter
                          ? formatter(row[key])
                          : row[key] ?? "—"}
                      </td>
                    )
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function R18Timeline({ rows }) {
  return (
    <div className="r18-card">
      <div className="r18-card-head">
        <h3>Traffic Timeline</h3>
        <p>
          Daily firewall and runtime observation
          volumes.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="r18-empty">
          No timeline data available.
        </div>
      ) : (
        <div className="r18-timeline">
          {rows.map((row, index) => (
            <div
              className="r18-timeline-row"
              key={index}
            >
              <div className="r18-timeline-top">
                <span>
                  {dateTime(
                    row.bucket_start
                  )}
                </span>

                <span>
                  {number(
                    row.event_count
                  )}{" "}
                  events
                </span>
              </div>

              <div className="r18-timeline-meta">
                <span>
                  Firewall:{" "}
                  {number(
                    row.firewall_observations
                  )}
                </span>

                <span>
                  Other:{" "}
                  {number(
                    row.runtime_or_other_observations
                  )}
                </span>

                <span>
                  High:{" "}
                  {number(
                    row.high_count
                  )}
                </span>

                <span>
                  Dropped:{" "}
                  {number(
                    row.dropped_count
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function CorrelationPanel({
  summary,
  loading,
  error,
  companyId,
  deviceUuid,
}) {
  const [evidence, setEvidence] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState("");

  useEffect(() => {
    setEvidence(null);
    setEvidenceError("");
    setEvidenceLoading(false);
  }, [companyId, deviceUuid]);

  const loadEvidence = async () => {
    if (!companyId || !deviceUuid || evidenceLoading) {
      return;
    }

    setEvidenceLoading(true);
    setEvidenceError("");

    try {
      const url = scopedUrl(
        "/network-protection/intelligence/correlation/evidence",
        companyId,
        deviceUuid
      );

      const response = await apiFetch(url);

      if (!response.ok) {
        throw new Error(
          `Correlation evidence request failed (${response.status})`
        );
      }

      const body = await response.json();

      setEvidence(
        body || null
      );
    } catch (requestError) {
      setEvidence(null);
      setEvidenceError(
        requestError?.message ||
          "Unable to load correlation evidence."
      );
    } finally {
      setEvidenceLoading(false);
    }
  };

  const state =
    summary?.correlation_state ||
    "insufficient_evidence";

  const confidence =
    summary?.confidence ||
    "none";

  const overlapCount = Number(
    summary?.temporally_overlapping_source_count || 0
  );

  const independentOverlapCount = Number(
    summary?.independent_overlapping_source_count || 0
  );

  const sourceCount = Number(
    summary?.source_count || 0
  );

  const networkEvidenceCount = Number(
    summary?.network_evidence_count || 0
  );

  const evidenceItems =
    evidence?.items ||
    summary?.source_status ||
    [];

  const stateLabel = String(state)
    .replaceAll("_", " ")
    .toUpperCase();

  const confidenceLabel = String(confidence)
    .replaceAll("_", " ")
    .toUpperCase();

  const stateTone =
    state === "multi_source_correlated"
      ? "#ef4444"
      : state === "single_source_evidence"
      ? "#f59e0b"
      : state === "context_only"
      ? "#38bdf8"
      : "#facc15";

  return (
    <section
      style={{
        marginTop: "22px",
        padding: "22px",
        borderRadius: "18px",
        border: "1px solid rgba(148,163,184,.18)",
        background:
          "linear-gradient(145deg, rgba(15,23,42,.96), rgba(2,6,23,.98))",
        boxShadow: "0 18px 45px rgba(0,0,0,.18)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "12px",
              textTransform: "uppercase",
              letterSpacing: ".12em",
              color: "#94a3b8",
              fontWeight: 700,
              marginBottom: "7px",
            }}
          >
            R18-P3 Correlation Intelligence
          </div>

          <h3
            style={{
              margin: 0,
              fontSize: "21px",
              lineHeight: 1.25,
              color: "#f8fafc",
            }}
          >
            Threat / UEBA / Endpoint Correlation
          </h3>

          <p
            style={{
              margin: "8px 0 0",
              maxWidth: "780px",
              color: "#94a3b8",
              fontSize: "14px",
              lineHeight: 1.55,
            }}
          >
            Correlates tenant-scoped network metadata with endpoint,
            authentication, UEBA, incident, threat-hunt, rule-match,
            and threat-intelligence evidence. Correlation does not
            establish causation.
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <span
            style={{
              border: `1px solid ${stateTone}`,
              color: stateTone,
              borderRadius: "999px",
              padding: "7px 11px",
              fontSize: "11px",
              fontWeight: 800,
              letterSpacing: ".05em",
            }}
          >
            {loading ? "CHECKING CORRELATION" : stateLabel}
          </span>

          <span
            style={{
              border: "1px solid rgba(148,163,184,.28)",
              color: "#cbd5e1",
              borderRadius: "999px",
              padding: "7px 11px",
              fontSize: "11px",
              fontWeight: 800,
            }}
          >
            CONFIDENCE: {confidenceLabel}
          </span>
        </div>
      </div>

      {error ? (
        <div
          style={{
            marginTop: "16px",
            padding: "12px 14px",
            borderRadius: "12px",
            border: "1px solid rgba(248,113,113,.35)",
            background: "rgba(127,29,29,.18)",
            color: "#fecaca",
            fontSize: "13px",
          }}
        >
          Correlation summary unavailable: {error}
        </div>
      ) : null}

      {!error && state === "no_temporal_corroboration" ? (
        <div
          style={{
            marginTop: "16px",
            padding: "14px 16px",
            borderRadius: "12px",
            border: "1px solid rgba(250,204,21,.28)",
            background: "rgba(113,63,18,.16)",
            color: "#fde68a",
            fontSize: "13px",
            lineHeight: 1.55,
          }}
        >
          <strong>No temporal corroboration.</strong>{" "}
          Network evidence and the available endpoint / security
          evidence do not meaningfully overlap in time. No current
          multi-source threat correlation is being asserted.
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "12px",
          marginTop: "18px",
        }}
      >
        {[
          [
            "Network Evidence",
            networkEvidenceCount.toLocaleString(),
          ],
          [
            "Available Sources",
            sourceCount.toLocaleString(),
          ],
          [
            "Overlapping Sources",
            overlapCount.toLocaleString(),
          ],
          [
            "Independent Overlap",
            independentOverlapCount.toLocaleString(),
          ],
        ].map(([label, value]) => (
          <div
            key={label}
            style={{
              padding: "15px",
              borderRadius: "13px",
              background: "rgba(15,23,42,.7)",
              border: "1px solid rgba(148,163,184,.14)",
            }}
          >
            <div
              style={{
                color: "#94a3b8",
                fontSize: "12px",
                marginBottom: "7px",
              }}
            >
              {label}
            </div>

            <div
              style={{
                color: "#f8fafc",
                fontSize: "21px",
                fontWeight: 800,
              }}
            >
              {loading ? "—" : value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          marginTop: "16px",
        }}
      >
        {[
          "Automatic blocking OFF",
          "Detection creation OFF",
          "Incident creation OFF",
          "Packet inspection NOT AVAILABLE",
          "DPI NOT AVAILABLE",
          "PCAP NOT AVAILABLE",
        ].map((item) => (
          <span
            key={item}
            style={{
              padding: "6px 9px",
              borderRadius: "999px",
              background: "rgba(30,41,59,.8)",
              border: "1px solid rgba(148,163,184,.16)",
              color: "#cbd5e1",
              fontSize: "11px",
              fontWeight: 700,
            }}
          >
            {item}
          </span>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          marginTop: "19px",
        }}
      >
        <div
          style={{
            color: "#94a3b8",
            fontSize: "12px",
          }}
        >
          Evidence details are loaded on demand so correlation analysis
          cannot delay the primary Network Intelligence refresh.
        </div>

        <button
          type="button"
          onClick={loadEvidence}
          disabled={
            evidenceLoading ||
            !companyId ||
            !deviceUuid
          }
          style={{
            border: "1px solid rgba(56,189,248,.35)",
            background: "rgba(14,116,144,.18)",
            color: "#bae6fd",
            borderRadius: "10px",
            padding: "9px 13px",
            fontSize: "12px",
            fontWeight: 800,
            cursor:
              evidenceLoading
                ? "wait"
                : "pointer",
            opacity:
              evidenceLoading
                ? 0.7
                : 1,
          }}
        >
          {evidenceLoading
            ? "Loading Evidence..."
            : evidence
            ? "Refresh Correlation Evidence"
            : "Load Correlation Evidence"}
        </button>
      </div>

      {evidenceError ? (
        <div
          style={{
            marginTop: "12px",
            color: "#fecaca",
            fontSize: "12px",
          }}
        >
          {evidenceError}
        </div>
      ) : null}

      {evidence ? (
        <div
          style={{
            overflowX: "auto",
            marginTop: "15px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "760px",
            }}
          >
            <thead>
              <tr>
                {[
                  "Source",
                  "Evidence",
                  "First Observed",
                  "Last Observed",
                  "Time Overlap",
                  "Independent",
                ].map((heading) => (
                  <th
                    key={heading}
                    style={{
                      textAlign: "left",
                      padding: "10px",
                      color: "#94a3b8",
                      fontSize: "11px",
                      borderBottom:
                        "1px solid rgba(148,163,184,.15)",
                    }}
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {evidenceItems.map(
                (item, index) => (
                  <tr
                    key={`${item?.source || "source"}-${index}`}
                  >
                    <td
                      style={{
                        padding: "10px",
                        color: "#e2e8f0",
                        fontSize: "12px",
                        borderBottom:
                          "1px solid rgba(148,163,184,.08)",
                      }}
                    >
                      {item?.source || "Unknown"}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        color: "#cbd5e1",
                        fontSize: "12px",
                        borderBottom:
                          "1px solid rgba(148,163,184,.08)",
                      }}
                    >
                      {Number(
                        item?.evidence_count || 0
                      ).toLocaleString()}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        color: "#94a3b8",
                        fontSize: "11px",
                        borderBottom:
                          "1px solid rgba(148,163,184,.08)",
                      }}
                    >
                      {item?.first_observed_at || "—"}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        color: "#94a3b8",
                        fontSize: "11px",
                        borderBottom:
                          "1px solid rgba(148,163,184,.08)",
                      }}
                    >
                      {item?.last_observed_at || "—"}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        color:
                          item?.temporally_overlaps_network
                            ? "#facc15"
                            : "#94a3b8",
                        fontSize: "12px",
                        borderBottom:
                          "1px solid rgba(148,163,184,.08)",
                      }}
                    >
                      {item?.temporally_overlaps_network
                        ? "YES"
                        : "NO"}
                    </td>

                    <td
                      style={{
                        padding: "10px",
                        color:
                          item?.independent_evidence
                            ? "#f59e0b"
                            : "#94a3b8",
                        fontSize: "12px",
                        borderBottom:
                          "1px solid rgba(148,163,184,.08)",
                      }}
                    >
                      {item?.independent_evidence
                        ? "YES"
                        : "NO"}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}


function BehavioralSignalsPanel({
  summary,
  signals,
  loading,
}) {
  const counts =
    summary?.candidate_counts || {};

  const state =
    summary?.state ||
    (loading
      ? "loading"
      : "insufficient_evidence");

  const stateLabel = String(state)
    .replaceAll("_", " ")
    .toUpperCase();

  const signalRows = [
    [
      "Port Scan",
      "port_scan_candidate",
    ],
    [
      "Host Sweep",
      "host_sweep_candidate",
    ],
    [
      "Beacon-like Timing",
      "beacon_like_timing",
    ],
    [
      "Rare Process / Destination",
      "rare_process_destination",
    ],
    [
      "Lateral Movement",
      "lateral_movement_indicator",
    ],
  ];

  return (
    <div className="r18-behavioral-section">
      <div className="r18-behavioral-head">
        <div>
          <span className="r18-kicker">
            R18-P2 BEHAVIORAL ANALYTICS
          </span>

          <h3>
            Network IDS &amp; Behavioral Signals
          </h3>

          <p>
            Read-only behavioral heuristics over
            runtime network metadata. Candidates
            are not confirmed intrusions and do
            not trigger automatic blocking.
          </p>
        </div>

        <span
          className={`r18-signal-state ${state}`}
        >
          {stateLabel}
        </span>
      </div>

      <div className="r18-signal-metrics">
        <R18Metric
          title="Current Signals"
          value={number(
            summary?.signal_count || 0
          )}
          detail="Behavioral candidates"
        />

        <R18Metric
          title="Runtime Evidence"
          value={number(
            summary?.runtime_or_other_events || 0
          )}
          detail="Non-firewall observations"
        />

        <R18Metric
          title="Reporting Devices"
          value={number(
            summary?.reporting_devices || 0
          )}
          detail="Tenant scoped"
        />

        <R18Metric
          title="Data Freshness"
          value={
            String(
              summary?.data_freshness ||
              "unknown"
            ).toUpperCase()
          }
          detail="Freshness affects confidence"
        />
      </div>

      <div className="r18-signal-grid">
        {signalRows.map(
          ([label, key]) => (
            <div
              className="r18-signal-card"
              key={key}
            >
              <span>{label}</span>

              <strong>
                {number(
                  counts?.[key] || 0
                )}
              </strong>

              <small>
                Candidate observations
              </small>
            </div>
          )
        )}
      </div>

      {state ===
      "insufficient_current_evidence" ? (
        <div className="r18-evidence-notice">
          <strong>
            No current IDS finding is being
            asserted.
          </strong>

          <p>
            The available network telemetry is
            stale. The engine found no scan,
            sweep, beacon-like, rare
            process/destination, or lateral
            movement candidates in the current
            evidence set.
          </p>
        </div>
      ) : null}

      {signals.length > 0 ? (
        <div className="r18-card">
          <div className="r18-card-head">
            <h3>Behavioral Candidates</h3>
            <p>
              Metadata-derived observations
              requiring analyst validation.
            </p>
          </div>

          <div className="r18-table-wrap">
            <table className="r18-table">
              <thead>
                <tr>
                  <th>Signal</th>
                  <th>Confidence</th>
                  <th>Device</th>
                  <th>Freshness</th>
                  <th>Observed</th>
                </tr>
              </thead>

              <tbody>
                {signals.map(
                  (signal, index) => (
                    <tr key={index}>
                      <td>
                        {String(
                          signal?.signal_type ||
                          "candidate"
                        ).replaceAll(
                          "_",
                          " "
                        )}
                      </td>

                      <td>
                        {signal?.confidence ||
                          "unknown"}
                      </td>

                      <td>
                        {signal?.device_uuid ||
                          "—"}
                      </td>

                      <td>
                        {signal?.data_freshness ||
                          "unknown"}
                      </td>

                      <td>
                        {dateTime(
                          signal?.last_observed_at ||
                          signal?.first_observed_at
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="r18-no-signals">
          <strong>
            Behavioral candidates: 0
          </strong>

          <span>
            No behavioral IDS candidate is
            currently supported by the
            available evidence.
          </span>
        </div>
      )}

      <div className="r18-behavioral-truth">
        <span>
          Automatic blocking: OFF
        </span>

        <span>
          Detection creation: OFF
        </span>

        <span>
          Packet inspection: NOT AVAILABLE
        </span>

        <span>
          DPI: NOT AVAILABLE
        </span>

        <span>
          PCAP: NOT AVAILABLE
        </span>
      </div>
    </div>
  );
}


function InspectionCapabilities() {
  return (
    <div className="r18-card">
      <div className="r18-card-head">
        <h3>Inspection Capabilities</h3>

        <p>
          Truthful distinction between telemetry
          analytics and packet inspection.
        </p>
      </div>

      <div className="r18-capabilities">
        {capabilityRows.map(
          ([label, available]) => (
            <div
              className="r18-capability"
              key={label}
            >
              <strong>{label}</strong>

              <span
                className={
                  available ? "yes" : "no"
                }
              >
                {available
                  ? "AVAILABLE"
                  : "NOT AVAILABLE"}
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );
}
